import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Building2, FilePlus, ShieldCheck, Settings, Layers, CreditCard,
  Activity, PauseCircle, Tv, Receipt, BarChart3, Lock, LifeBuoy,
  Download, ArrowLeft, ChevronRight, HelpCircle,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  Building2, FilePlus, ShieldCheck, Settings, Layers, CreditCard,
  Activity, PauseCircle, Tv, Receipt, BarChart3, Lock, LifeBuoy,
};

export default function HelpEstablishment() {
  const [data, setData] = useState(null);
  const [activeSection, setActiveSection] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/help/establishment`).then((r) => {
      setData(r.data);
      if (r.data.sections?.[0]) setActiveSection(r.data.sections[0].id);
    });
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="help-establishment-loading">
        Chargement du centre d'aide…
      </div>
    );
  }

  const section = data.sections.find((s) => s.id === activeSection) || data.sections[0];
  const Icon = section ? ICONS[section.icon] || HelpCircle : HelpCircle;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="help-establishment-page">
      <div className="bg-gradient-to-br from-[#002FA7] to-[#003FC7] text-white">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-6"
            data-testid="help-establishment-back-home"
          >
            <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
          </Link>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="uppercase tracking-widest text-white/60 text-xs mb-2">Centre d'aide</p>
              <h1 className="text-4xl md:text-5xl font-bold leading-tight">{data.title}</h1>
              <p className="text-white/80 mt-3 text-lg max-w-2xl">{data.subtitle}</p>
            </div>
            <a
              href={`${API}/api/help/pdf/establishment`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-[#002FA7] px-5 py-3 rounded-lg font-semibold hover:bg-blue-50 transition shadow-lg"
              data-testid="help-establishment-download-pdf"
            >
              <Download className="w-5 h-5" /> Télécharger le guide PDF
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10 grid md:grid-cols-[280px_1fr] gap-8">
        <aside className="md:sticky md:top-6 self-start space-y-1" data-testid="help-establishment-sidebar">
          {data.sections.map((s) => {
            const SecIcon = ICONS[s.icon] || HelpCircle;
            return (
              <button
                key={s.id}
                onClick={() => setActiveSection(s.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition ${
                  activeSection === s.id
                    ? "bg-[#002FA7] text-white"
                    : "text-slate-700 hover:bg-slate-100"
                }`}
                data-testid={`help-establishment-nav-${s.id}`}
              >
                <SecIcon className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{s.title}</span>
              </button>
            );
          })}
          <button
            onClick={() => setActiveSection("faq")}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition ${
              activeSection === "faq"
                ? "bg-[#002FA7] text-white"
                : "text-slate-700 hover:bg-slate-100"
            }`}
            data-testid="help-establishment-nav-faq"
          >
            <HelpCircle className="w-4 h-4 flex-shrink-0" />
            <span>FAQ</span>
          </button>
        </aside>

        <main className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-10" data-testid="help-establishment-content">
          {activeSection === "faq" ? (
            <FaqBlock items={data.faq} />
          ) : (
            <SectionView section={section} Icon={Icon} />
          )}
        </main>
      </div>
    </div>
  );
}

function SectionView({ section, Icon }) {
  return (
    <article>
      <header className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-[#002FA7]/10 flex items-center justify-center">
          <Icon className="w-6 h-6 text-[#002FA7]" />
        </div>
        <h2 className="text-3xl font-bold text-slate-900">{section.title}</h2>
      </header>

      {section.intro && (
        <p className="text-slate-600 text-lg leading-relaxed mb-6">{section.intro}</p>
      )}

      {section.warning && (
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg mb-6 text-amber-900 text-sm">
          ⚠️ {section.warning}
        </div>
      )}
      {section.tip && (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-6 text-blue-900 text-sm">
          💡 {section.tip}
        </div>
      )}
      {section.note && (
        <div className="bg-slate-50 border-l-4 border-slate-400 p-4 rounded-r-lg mb-6 text-slate-700 text-sm">
          {section.note}
        </div>
      )}

      {section.steps && (
        <ol className="space-y-3 mb-6">
          {section.steps.map((step, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#002FA7] text-white text-sm font-bold flex items-center justify-center">
                {i + 1}
              </span>
              <span className="text-slate-700 pt-1">{step}</span>
            </li>
          ))}
        </ol>
      )}

      {section.items && (
        <ul className="space-y-3 mb-6">
          {section.items.map((it, i) => (
            <li key={i} className="flex gap-3">
              <ChevronRight className="w-5 h-5 text-[#002FA7] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-900">{it.label}</p>
                <p className="text-slate-600 text-sm">{it.detail}</p>
              </div>
            </li>
          ))}
        </ul>
      )}

      {section.fields && (
        <div className="border border-slate-200 rounded-lg overflow-hidden mb-6">
          <table className="w-full text-sm">
            <thead className="bg-[#002FA7] text-white">
              <tr>
                <th className="px-4 py-3 text-left">Champ</th>
                <th className="px-4 py-3 text-left">Impact</th>
              </tr>
            </thead>
            <tbody>
              {section.fields.map((f, i) => (
                <tr key={i} className="border-t border-slate-200">
                  <td className="px-4 py-3 font-medium">{f.label}</td>
                  <td className="px-4 py-3 text-slate-600">{f.impact}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {section.modes && (
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {section.modes.map((m, i) => (
            <div key={i} className="border-2 border-[#002FA7] rounded-xl p-5 bg-white shadow-sm">
              <h3 className="text-xl font-bold text-[#002FA7] mb-1">{m.name}</h3>
              <p className="text-2xl font-bold text-slate-900 mb-1">{m.price}</p>
              <p className="text-sm text-slate-500 italic mb-4">Idéal : {m.ideal}</p>
              <ul className="space-y-1.5 text-sm text-slate-700">
                {m.details.map((d, j) => (
                  <li key={j} className="flex items-start gap-2">
                    <span className="text-[#002FA7] font-bold">✓</span> {d}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {section.channels && (
        <div className="grid sm:grid-cols-2 gap-3 mb-6">
          {section.channels.map((c, i) => (
            <div key={i} className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">{c.label}</p>
              <p className="font-medium text-slate-900">{c.detail}</p>
            </div>
          ))}
        </div>
      )}

      {section.sla && (
        <div className="border border-slate-200 rounded-lg overflow-hidden mb-6">
          <table className="w-full text-sm">
            <thead className="bg-[#002FA7] text-white">
              <tr>
                <th className="px-4 py-3 text-left">Niveau</th>
                <th className="px-4 py-3 text-left">Délai SLA</th>
              </tr>
            </thead>
            <tbody>
              {section.sla.map((s, i) => (
                <tr key={i} className="border-t border-slate-200">
                  <td className="px-4 py-3 font-medium">{s.level}</td>
                  <td className="px-4 py-3 text-slate-600">{s.delay}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {section.screenshot && (
        <figure className="mt-8" data-testid="help-screenshot">
          <img
            src={`${API}/api/help/screenshot/${section.screenshot}`}
            alt={section.title}
            className="rounded-lg border border-slate-200 shadow-sm w-full"
            onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
          />
          <div className="hidden bg-slate-100 border border-dashed border-slate-300 rounded-lg p-8 text-center text-slate-400 italic">
            📷 Capture : {section.screenshot}
          </div>
          <figcaption className="text-center text-xs text-slate-500 italic mt-2">
            Aperçu — {section.title}
          </figcaption>
        </figure>
      )}
    </article>
  );
}

function FaqBlock({ items }) {
  const [open, setOpen] = useState(null);
  return (
    <article>
      <header className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-[#002FA7]/10 flex items-center justify-center">
          <HelpCircle className="w-6 h-6 text-[#002FA7]" />
        </div>
        <h2 className="text-3xl font-bold text-slate-900">Questions fréquentes</h2>
      </header>
      <div className="space-y-2">
        {items.map((item, i) => (
          <details
            key={i}
            open={open === i}
            onToggle={(e) => setOpen(e.target.open ? i : null)}
            className="bg-slate-50 rounded-lg border border-slate-200 group"
          >
            <summary className="cursor-pointer px-4 py-3 font-medium text-slate-900 hover:bg-slate-100 rounded-lg list-none flex items-center justify-between">
              <span>{item.q}</span>
              <ChevronRight className="w-5 h-5 text-slate-400 group-open:rotate-90 transition" />
            </summary>
            <p className="px-4 pb-4 text-slate-600">{item.a}</p>
          </details>
        ))}
      </div>
    </article>
  );
}
